// src/ui/RelicBar.ts

import Phaser from 'phaser';
import type { Rarity } from '../state/types';
import { addCrispText } from './textHelper';
import { relicSpritesheet } from '../content/relics';

export type { Rarity };

export interface RelicSlot {
  defId: string;
  name: string;
  description: string;
  rarity: Rarity;
}

const RARITY_STROKE: Record<Rarity, number> = {
  common: 0x888888,
  uncommon: 0x4a90e2,
  rare: 0xa259d6,
  legendary: 0xffd700,
};

export class RelicBar extends Phaser.GameObjects.Container {
  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    scene.add.existing(this);
    this.draw([]);
  }

  draw(relics: ReadonlyArray<RelicSlot>): void {
    this.removeAll(true);
    const slotW = 113;
    const slotH = 70;
    const gap = 8;
    const margin = 8;

    for (let i = 0; i < 3; i++) {
      const sx = margin + i * (slotW + gap);
      const isFilled = i < relics.length;

      const strokeColor = isFilled ? RARITY_STROKE[relics[i]!.rarity] : 0x444444;
      const rect = this.scene.add
        .rectangle(sx, 0, slotW, slotH, 0x111111)
        .setOrigin(0, 0)
        .setStrokeStyle(1, strokeColor);
      this.add(rect);

      if (isFilled) {
        const relic = relics[i]!;
        const sheet = relicSpritesheet(relic.defId);
        const texKey = `relic-${relic.defId}`;
        const hasImg =
          (sheet && this.scene.textures.exists(sheet.key)) || this.scene.textures.exists(texKey);

        if (hasImg) {
          const img =
            sheet && this.scene.textures.exists(sheet.key)
              ? this.scene.add
                  .image(sx + slotW / 2, slotH / 2, sheet.key, sheet.frame)
                  .setDisplaySize(slotW - 2, slotH - 2)
                  .setOrigin(0.5)
              : this.scene.add
                  .image(sx + slotW / 2, slotH / 2, texKey)
                  .setDisplaySize(slotW - 2, slotH - 2)
                  .setOrigin(0.5);
          img.setInteractive({ useHandCursor: true });
          img.on('pointerdown', () => this._openModal(relic));
          this.add(img);
        } else {
          // Text-only fallback — still tappable
          const label = addCrispText(this.scene, sx + slotW / 2, slotH / 2, relic.name, {
            color: '#ffffff',
            fontSize: '10px',
            align: 'center',
            wordWrap: { width: slotW - 6 },
          }).setOrigin(0.5);
          label.setInteractive({ useHandCursor: true });
          label.on('pointerdown', () => this._openModal(relic));
          this.add(label);
        }
      } else {
        const label = addCrispText(this.scene, sx + slotW / 2, slotH / 2, '—', {
          color: '#444444',
          fontSize: '11px',
        }).setOrigin(0.5);
        this.add(label);
      }
    }
  }

  private _openModal(relic: RelicSlot): void {
    const c = this.scene.add.container(0, 0).setDepth(60);

    const backdrop = this.scene.add
      .rectangle(187, 333, 375, 667, 0x000000, 0.82)
      .setOrigin(0.5)
      .setInteractive();
    backdrop.on('pointerdown', () => c.destroy());
    c.add(backdrop);

    // Title
    c.add(
      addCrispText(this.scene, 187, 80, relic.name, {
        color: '#d4af37',
        fontSize: '18px',
      }).setOrigin(0.5),
    );

    // Image
    const sheet = relicSpritesheet(relic.defId);
    const texKey = `relic-${relic.defId}`;
    if (sheet && this.scene.textures.exists(sheet.key)) {
      c.add(
        this.scene.add
          .image(187, 230, sheet.key, sheet.frame)
          .setDisplaySize(220, 110)
          .setOrigin(0.5),
      );
    } else if (this.scene.textures.exists(texKey)) {
      c.add(this.scene.add.image(187, 230, texKey).setDisplaySize(160, 160).setOrigin(0.5));
    }

    // Description
    c.add(
      addCrispText(this.scene, 187, 300, relic.description, {
        color: '#cccccc',
        fontSize: '12px',
        wordWrap: { width: 300 },
        align: 'center',
      }).setOrigin(0.5, 0),
    );

    // Close button
    const closeBtn = this.scene.add
      .rectangle(187, 470, 160, 40, 0x333333)
      .setStrokeStyle(1, 0x888888)
      .setInteractive({ useHandCursor: true });
    c.add(closeBtn);
    c.add(
      addCrispText(this.scene, 187, 470, 'Close', {
        color: '#ffffff',
        fontSize: '13px',
      }).setOrigin(0.5),
    );
    closeBtn.on('pointerdown', () => c.destroy());
  }
}
